/**
 * @module components/SearchableSelect
 * @description Generischer Searchable-Select (Combobox) für Listen mit vielen Einträgen.
 * Client-side Fuzzy-Match (Substring + Token-Match), Tastatur-Navigation, Touch-fähig.
 *
 * #995 Punkt 6 — Ersatz für native <select> in Zeiterfassung-Modul (143+ Kunden).
 * Wiederverwendbar: nimmt `options: [{ value, label, sublabel? }, ...]` und liefert value-String.
 *
 * Verwendung:
 *   <SearchableSelect
 *     value={kundeId}
 *     onChange={setKundeId}
 *     options={[{value:'', label:'— optional —'}, ...kunden.map(k => ({value:String(k.id), label:k.name}))]}
 *     placeholder="Kunde suchen…"
 *   />
 *
 * @since v2.431.2
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

/**
 * Fuzzy-Match: liefert score >= 0 wenn alle Tokens des Such-Strings im Label vorkommen.
 * Höher = besser (Bonus für Prefix, Bonus wenn alle Tokens als zusammenhängender Substring).
 */
function fuzzyScore(needle, hay) {
  if (!needle) return 1;
  const n = needle.toLowerCase().trim();
  const h = (hay || '').toLowerCase();
  if (!h) return 0;
  if (h === n) return 1000;
  if (h.startsWith(n)) return 500;
  if (h.includes(n)) return 250;
  // Token-Match: alle Tokens müssen vorkommen (Reihenfolge egal)
  const tokens = n.split(/\s+/).filter(Boolean);
  if (!tokens.length) return 0;
  let score = 0;
  for (const tok of tokens) {
    const idx = h.indexOf(tok);
    if (idx < 0) return 0;
    score += idx === 0 ? 50 : 10;
  }
  return score;
}

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Suchen…',
  disabled = false,
  className = '',
  size = 'md', // 'sm' | 'md'
  emptyText = 'Keine Treffer',
  clearable = false,
  inputClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value ?? '')),
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const scored = options
      .map(o => {
        const labelScore = fuzzyScore(query, o.label || '');
        const subScore = o.sublabel ? fuzzyScore(query, o.sublabel) * 0.7 : 0;
        const score = Math.max(labelScore, subScore);
        return { o, score };
      })
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.map(x => x.o);
  }, [options, query]);

  useEffect(() => { setActiveIdx(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Auto-scroll active item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx, open]);

  const handleSelect = useCallback((opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) handleSelect(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIdx(filtered.length - 1);
    }
  }, [open, filtered, activeIdx, handleSelect]);

  const sizing = size === 'sm'
    ? 'px-2.5 py-1.5 text-[13px]'
    : 'px-3 py-2 text-[14px]';

  const displayLabel = selected?.label ?? '';

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        className={`w-full ${sizing} rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] flex items-center justify-between gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'} ${inputClassName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate text-left flex-1 ${displayLabel ? '' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
          {displayLabel || placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {clearable && selected && selected.value !== '' && (
            <X
              className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"
              onClick={(e) => { e.stopPropagation(); onChange?.(''); }}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-[var(--text-tertiary,#6B7280)] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-[90] mt-1 w-full bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[10px] shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
            <Search className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full bg-transparent outline-none text-[13px] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-tertiary,#6B7280)]"
            />
          </div>
          <div ref={listRef} className="max-h-[280px] overflow-y-auto" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[13px] text-[var(--text-tertiary,#6B7280)] text-center">{emptyText}</div>
            ) : (
              filtered.map((o, idx) => {
                const isSelected = String(o.value) === String(value ?? '');
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={`${o.value}-${idx}`}
                    data-idx={idx}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(o); }}
                    className={`px-3 py-2 text-[13px] cursor-pointer flex items-center justify-between gap-2 ${isActive ? 'bg-[var(--bg-inset,#F8F9FC)]' : ''} ${isSelected ? 'text-[#338edc] font-medium' : 'text-[var(--text-primary,#1A1A2E)]'}`}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate">{o.label}</span>
                      {o.sublabel && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] truncate">{o.sublabel}</span>}
                    </div>
                    {isSelected && <span className="text-[#338edc] flex-shrink-0">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
